import { MAX_UPLOAD_MB } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, DismissibleBanner, Icon, type IconName } from "@sagot/ui";
import type { SourceView } from "../shared/types";

const K = "deployKnowledge.knowledge";

/** Warning banner listing failed sources with their (server-provided, user-safe) reasons. */
export function AttentionBanner({ failed, onRetry, canRetry }: { failed: SourceView[]; onRetry: (source: SourceView) => void; canRetry: boolean }) {
  const { t } = useTranslation();
  const first = failed[0];
  if (!first) return null;
  const key = `knowledge-attention:${failed.map((s) => `${s.id}@${s.updated_at}`).join(",")}`;
  return (
    <DismissibleBanner
      tone="warning"
      storageKey={key}
      dismissLabel={t(`${K}.banner.dismiss`)}
      title={t(`${K}.banner.title`, { count: failed.length })}
      actions={
        canRetry ? (
          <Button variant="secondary" size="sm" onClick={() => { onRetry(first); }}>
            <Icon name="refresh-cw" />
            {t(`${K}.banner.retry`, { name: first.title })}
          </Button>
        ) : undefined
      }
    >
      {t(`${K}.banner.body`, { name: first.title, reason: first.status_reason ?? t(`${K}.banner.noReason`) })}
      {failed.length > 1 ? ` ${t(`${K}.banner.bodyMore`, { count: failed.length - 1 })}` : null}
    </DismissibleBanner>
  );
}

/** Big dashed call-to-action under the table (opens the Add-source dialog). */
export function DropCallout({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full flex-col items-center justify-center gap-2 rounded-card border-2 border-dashed border-line-strong bg-surface px-6 py-8 text-center transition-colors hover:border-primary hover:bg-primary-soft"
    >
      <span className="inline-flex size-11 items-center justify-center rounded-pill bg-primary-soft text-primary-ink">
        <Icon name="upload-cloud" className="size-5" />
      </span>
      <span className="text-sm font-bold">{t(`${K}.callout.title`)}</span>
      <span className="text-xs text-fg-muted">{t(`${K}.callout.body`, { mb: MAX_UPLOAD_MB })}</span>
    </button>
  );
}

const STEPS: readonly { key: string; icon: IconName }[] = [
  { key: "parsed", icon: "scan-text" },
  { key: "chunked", icon: "layers" },
  { key: "embedded", icon: "cpu" },
  { key: "searched", icon: "search" },
  { key: "reranked", icon: "list-checks" },
  { key: "answered", icon: "quote" },
];

/** "How your documents are used": the six-step RAG pipeline explainer. */
export function PipelineCard() {
  const { t } = useTranslation();
  const last = STEPS.length - 1;
  return (
    <section className="card" aria-labelledby="pipeline-title">
      <div className="card-header">
        <div>
          <h2 id="pipeline-title" className="card-title">
            {t(`${K}.pipeline.title`)}
          </h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t(`${K}.pipeline.subtitle`)}</p>
        </div>
      </div>
      <div className="card-body">
        <ol className="grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 lg:grid-cols-6">
          {STEPS.map((step, index) => (
            <li key={step.key} className="relative flex flex-col items-center text-center">
              <span
                className={
                  index === last
                    ? "relative z-10 inline-flex size-11 items-center justify-center rounded-pill bg-primary text-on-primary"
                    : "relative z-10 inline-flex size-11 items-center justify-center rounded-pill border border-line bg-surface text-primary-ink"
                }
              >
                <Icon name={step.icon} className="size-5" />
              </span>
              {index < last ? <span aria-hidden="true" className="absolute left-1/2 top-[22px] hidden h-px w-full bg-line-strong lg:block" /> : null}
              <span className="mt-2.5 font-mono text-[10px] font-bold text-fg-subtle">{String(index + 1).padStart(2, "0")}</span>
              <span className="text-sm font-bold">{t(`${K}.pipeline.${step.key}`)}</span>
              <span className="mt-1 text-xs text-fg-muted">{t(`${K}.pipeline.${step.key}Body`)}</span>
            </li>
          ))}
        </ol>
        <div className="mt-5 flex items-start gap-2.5 rounded-md bg-surface-2 px-3 py-2.5 text-xs text-fg-muted">
          <Icon name="shield-check" className="mt-0.5 size-4 shrink-0 text-primary-ink" />
          <span>{t(`${K}.pipeline.residency`)}</span>
        </div>
      </div>
    </section>
  );
}
